import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { useAdminOrCoordinatorSession } from '../../context/TrackerContext';
import { getAdminDashboardStats, getAdminNotifications, type AdminNotificationCounts, type ClassSeriesPoint, type DashboardReport, type RanAwayMentorRow } from '../../lib/api';
import {
  C, attStatus, num, inr, hours, plural, pctOf, fmtWeekday, fmtRange, monthName, shiftMonth, addDays, delta,
  previousPeriod, periodFromParams, periodParams, type Period, type ActiveFilter,
  ReportHeader, HeaderButton, PeriodPicker, SelectFilter, ToggleChip, FilterCard, Section, Card, KpiTile, HowCounted,
  ColumnChart, StackBar, AttendanceCell, Notice, Loading, ServerOutdated, LinkButton,
} from '../../components/ReportKit';

const PHASES: { phase: string; label: string; color: string }[] = [
  { phase: 'First 5 Days', label: 'First 5 days', color: C.phases[0] },
  { phase: 'Mid Course',   label: 'Mid course',   color: C.phases[1] },
  { phase: 'Last 10 Days', label: 'Last 10 days', color: C.phases[2] },
  { phase: '',             label: 'Phase not set', color: '#d1d5db' },
];

const UNIT_LABEL = { day: 'Day', week: 'Week', month: 'Month' } as const;

function slotTitle(p: ClassSeriesPoint, unit: 'day' | 'week' | 'month') {
  if (unit === 'day') return fmtWeekday(p.key);
  if (unit === 'week') return `Week of ${fmtRange(p.from, p.to)}`;
  return p.from.slice(8) === '01' && p.to === addDays(`${shiftMonth(p.key, 1)}-01`, -1) ? monthName(p.key) : fmtRange(p.from, p.to);
}

// Does month 'YYYY-MM' overlap the period?
const monthInPeriod = (month: string, p: Period) =>
  (!p.from || p.from <= addDays(`${shiftMonth(month, 1)}-01`, -1)) && `${month}-01` <= p.to;

// ── "Needs your attention" tile ───────────────────────────────────────────────
const AttentionTile: React.FC<{ icon: string; label: string; count: number; color: string; onClick: () => void }> = ({ icon, label, count, color, onClick }) => (
  <button onClick={onClick}
    className="text-left bg-white rounded-2xl p-4 shadow-sm border-2 transition-all hover:shadow-md"
    style={{ borderColor: count > 0 ? color : '#f3f4f6' }}>
    <div className="flex items-start justify-between mb-2">
      <span className="text-xl">{icon}</span>
      <span className="text-2xl font-black" style={{ color: count > 0 ? color : '#d1d5db' }}>{count}</span>
    </div>
    <p className="text-xs font-semibold text-gray-700">{label}</p>
    <p className="text-xs mt-0.5" style={{ color: count > 0 ? color : '#9ca3af' }}>{count > 0 ? 'Open →' : 'All clear'}</p>
  </button>
);

// ── Ran away, by mentor — the top few; the Ran Away page has them all ─────────
const RanAwayCard: React.FC<{ rows: RanAwayMentorRow[]; onOpen: (mentorId?: string) => void }> = ({ rows, onOpen }) => {
  const top = rows.filter(r => r.ranAway > 0).slice(0, 5);
  const max = Math.max(1, ...top.map(r => r.ranAway));
  return (
    <Card title="Whose students ran away" subtitle="Now · everyone, whenever they joined · most first. Click a mentor for their students."
      right={<LinkButton onClick={() => onOpen()}>Ran Away by Mentor →</LinkButton>}>
      {top.length === 0 ? <p className="text-xs text-gray-400">Nobody has run away</p> : (
        <div className="space-y-2">
          {top.map(r => (
            <button key={r.mentorId} onClick={() => onOpen(r.mentorId)}
              className="w-full grid grid-cols-[minmax(0,8rem)_1fr_6.5rem] sm:grid-cols-[minmax(0,12rem)_1fr_7rem] items-center gap-3 text-left rounded-lg px-1 py-0.5 hover:bg-gray-50">
              <span className="text-xs font-semibold text-gray-700 truncate">{r.name}{r.status === 'Inactive' && <span className="font-normal text-gray-400"> (inactive)</span>}</span>
              <span className="h-2.5 rounded-sm bg-gray-100 overflow-hidden">
                <span className="block h-full rounded-sm" style={{ width: `${(r.ranAway / max) * 100}%`, background: C.critical }} />
              </span>
              <span className="text-xs tabular-nums text-gray-500 whitespace-nowrap text-right">
                <b className="text-gray-800">{num(r.ranAway)}</b> of {num(r.students)} · {r.rate}%
              </span>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
};

// ── main page ─────────────────────────────────────────────────────────────────
// Filters: period, course, mentor (and whether to compare with the period
// before). Classes and new students follow the period; tiles marked Now are as
// of today. Every filter is kept in the page address.

const AdminDashboard: React.FC = () => {
  const { adminPassword } = useAdminOrCoordinatorSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const isAdmin = !adminPassword.startsWith('COORD:');

  const [period, setPeriod]     = useState<Period>(() => periodFromParams(params, 'month'));
  const [course, setCourse]     = useState(() => params.get('course') || '');
  const [mentorId, setMentorId] = useState(() => params.get('mentor') || '');
  const [compare, setCompare]   = useState(() => params.get('compare') !== 'off');

  const [data, setData]           = useState<DashboardReport | null>(null);
  const [attention, setAttention] = useState<AdminNotificationCounts | null>(null);
  const [loading, setLoading]     = useState(true);
  const [outdated, setOutdated]   = useState(false);
  const latest = useRef(0);

  const prev = useMemo(() => (compare ? previousPeriod(period) : null), [compare, period]);
  const prevFrom = prev ? prev.from : '';
  const prevTo   = prev ? prev.to : '';

  const load = useCallback(async () => {
    const call = ++latest.current;
    setLoading(true);
    try {
      const res = await getAdminDashboardStats(adminPassword,
        { from: period.from, to: period.to, ...(prevFrom ? { prevFrom, prevTo } : {}) }, { course, mentorId });
      if (call !== latest.current) return;
      setOutdated(!res.range);
      if (res.range) setData(res);
    } catch (err: any) {
      if (call === latest.current) toast.error(err.message);
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [adminPassword, period.from, period.to, prevFrom, prevTo, course, mentorId]);

  // Same counts as the notification bell — admin only
  const loadAttention = useCallback(() => {
    if (!isAdmin) return;
    getAdminNotifications(adminPassword).then(r => setAttention(r.counts)).catch(() => setAttention(null));
  }, [adminPassword, isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAttention(); }, [loadAttention]);

  useEffect(() => {
    const next = new URLSearchParams(periodParams(period));
    if (course) next.set('course', course);
    if (mentorId) next.set('mentor', mentorId);
    if (!compare) next.set('compare', 'off');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [period, course, mentorId, compare, params, setParams]);

  const mentorName = data?.options.mentors.find(m => m.mentorId === mentorId)?.name || mentorId;
  const scope = [course || 'All courses', mentorId ? mentorName : 'All mentors'].join(' · ');
  const vs = prev ? fmtRange(prev.from, prev.to) : '';

  const active: ActiveFilter[] = [
    ...(course ? [{ label: `Course: ${course}`, onRemove: () => setCourse('') }] : []),
    ...(mentorId ? [{ label: `Mentor: ${mentorName}`, onRemove: () => setMentorId('') }] : []),
  ];

  const openMentorPerf = (extra: Record<string, string>) => navigate(`/tracker/admin/mentor-perf?${new URLSearchParams({
    ...periodParams(period), ...(course ? { course } : {}), ...extra,
  })}`);
  const openRevenue = (extra: Record<string, string>) => navigate(`/tracker/admin/revenue?${new URLSearchParams({
    ...(course ? { course } : {}), ...(mentorId ? { mentor: mentorId } : {}), ...extra,
  })}`);
  const openRanAway = (mentor?: string) => navigate(`/tracker/admin/ran-away?${new URLSearchParams({
    ...(course ? { course } : {}), ...(mentor || mentorId ? { mentor: mentor || mentorId } : {}),
  })}`);

  return (
    <TrackerLayout>
      <div className="space-y-6">
        <ReportHeader icon="📊" title="Admin Dashboard"
          intro="A quick picture of students, classes and fees. Pick a period and filters below — tiles marked In period follow the dates you pick; tiles marked Now are as of today."
          actions={<HeaderButton onClick={() => { load(); loadAttention(); }}>↻ Refresh</HeaderButton>} />

        <FilterCard busy={loading && !!data} active={active} onClearAll={() => { setCourse(''); setMentorId(''); }}
          showing={<>Showing <b className="text-gray-700">{fmtRange(period.from, period.to)}</b>{vs && <> · compared with <b className="text-gray-700">{vs}</b></>}</>}>
          <PeriodPicker label="Period" value={period} onChange={setPeriod} />
          <div className="flex flex-wrap items-end gap-3">
            <SelectFilter label="Course" value={course} onChange={setCourse} allLabel="All courses"
              options={(data?.options.courses ?? (course ? [course] : [])).map(c => ({ value: c, label: c }))} />
            <SelectFilter label="Mentor" value={mentorId} onChange={setMentorId} allLabel="All mentors"
              options={(data?.options.mentors ?? []).map(m => ({ value: m.mentorId, label: m.status === 'Inactive' ? `${m.name} (inactive)` : m.name }))} />
            <ToggleChip on={compare} onClick={() => setCompare(v => !v)} title="Show ▲ / ▼ against the same stretch just before">
              {compare ? '✓ ' : ''}Compare with the period before
            </ToggleChip>
          </div>
        </FilterCard>

        {outdated && <ServerOutdated />}

        {!data ? (loading ? <Loading text="Loading dashboard…" /> : null) : (
          <div className={`space-y-8 transition-opacity ${loading ? 'opacity-60' : ''}`}>

            {/* ── needs your attention ── */}
            <Section title="Needs your attention" caption="Live — the filters above don't apply here">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {attention && (
                  <>
                    <AttentionTile icon="🙋" label="Payment dates to approve" count={attention.paymentDateRequests} color="#d97706"
                      onClick={() => navigate('/tracker/admin/payment-dates')} />
                    <AttentionTile icon="📅" label="Payments due today" count={attention.paymentsDueToday} color="#15803d"
                      onClick={() => navigate('/tracker/admin/payments')} />
                    <AttentionTile icon="⏰" label="Overdue payments" count={attention.paymentsOverdue} color="#dc2626"
                      onClick={() => navigate('/tracker/admin/payments')} />
                    <AttentionTile icon="🎓" label="New Class Over" count={attention.classOver} color="#0369a1"
                      onClick={() => navigate('/tracker/admin/class-over')} />
                  </>
                )}
                <AttentionTile icon="🧑‍💼" label="Leads waiting for a mentor" count={data.unassignedLeads} color="#7c3aed"
                  onClick={() => navigate('/tracker/admin/unassigned')} />
              </div>
            </Section>

            {/* ── students ── */}
            <Section title="Students" caption={scope}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiTile label="Active students" scope="now" value={num(data.students.active)}
                  hint={`${plural(data.students.total, 'student')} in all — ${num(data.students.total - data.students.active)} not active right now`} />
                <KpiTile label="New students" scope="period" value={num(data.students.joined)}
                  delta={delta(data.students.joined, data.students.joinedPrev, vs)}
                  hint="Joined during the period" />
                <KpiTile label="At risk" scope="now" value={num(data.students.atRisk)}
                  status={data.students.atRisk > 0 ? { tone: 'critical', label: 'Follow up' } : { tone: 'good', label: 'None' }}
                  hint="Active students who missed their last 3 classes in a row"
                  onClick={() => openMentorPerf({ follow: 'atRisk' })} cta="See who" />
                <KpiTile label="No class in 7+ days" scope="now" value={num(data.students.idle)}
                  status={data.students.idle > 0 ? { tone: 'warning', label: 'Check in' } : { tone: 'good', label: 'None' }}
                  hint="Active students who haven't attended a class for a week or more"
                  onClick={() => openMentorPerf({ follow: 'idle' })} cta="See who" />
              </div>

              <div className="grid lg:grid-cols-3 gap-3">
                <Card className="lg:col-span-2" title="New students per month"
                  subtitle="Last 12 months, by joining date. Darker months fall in your period.">
                  <ColumnChart firstColumn="Month" series={[{ name: 'New students', color: C.bar }]} height={130}
                    points={data.enrollments.map(e => ({
                      key: e.key, label: e.label, title: monthName(e.key), values: [e.count], muted: !monthInPeriod(e.key, period),
                    }))} />
                </Card>
                <div className="space-y-3">
                  <Card title="Where active students are" subtitle="Course phase, as set by their mentor">
                    <StackBar columns="grid-cols-2"
                      parts={PHASES
                        .map(p => ({ label: p.label, color: p.color, value: data.students.phases.find(x => x.phase === p.phase)?.count ?? 0 }))
                        .filter(p => p.label !== 'Phase not set' || p.value > 0)} />
                  </Card>
                  <Card title="All students by status">
                    <dl className="space-y-1.5">
                      {([
                        ['Active', data.students.active],
                        ['Inactive', data.students.inactive],
                        ['Ran away', data.students.ranAway],
                        ['Institute leave', data.students.instituteLeave],
                      ] as [string, number][]).map(([label, count]) => (
                        <div key={label} className="flex items-center justify-between text-xs">
                          <dt className="text-gray-500">{label}</dt>
                          <dd className="font-bold text-gray-800 tabular-nums">{num(count)}</dd>
                        </div>
                      ))}
                    </dl>
                  </Card>
                </div>
              </div>

              {data.ranAwayByMentor && <RanAwayCard rows={data.ranAwayByMentor} onOpen={openRanAway} />}
            </Section>

            {/* ── classes ── */}
            <Section title="Classes & attendance" caption={`${fmtRange(period.from, period.to)} · ${scope}`}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiTile label="Classes held" scope="period" value={num(data.classes.held)}
                  delta={delta(data.classes.held, data.classes.prev?.held, vs)}
                  hint="Classes logged with the student present" />
                <KpiTile label="No-shows" scope="period" value={num(data.classes.noShows)}
                  delta={delta(data.classes.noShows, data.classes.prev?.noShows, vs, { upIsGood: false })}
                  hint="Classes logged with the student absent" />
                <KpiTile label="Attendance rate" scope="period"
                  value={data.classes.attPct == null ? '—' : `${data.classes.attPct}%`}
                  status={attStatus(data.classes.attPct)}
                  delta={delta(data.classes.attPct, data.classes.prev?.attPct, vs, { unit: ' pts' })}
                  hint="Present ÷ (present + absent). Leave days aren't counted." />
                <KpiTile label="Teaching hours" scope="period" value={hours(data.classes.minutes)}
                  delta={delta(data.classes.minutes / 60, data.classes.prev ? data.classes.prev.minutes / 60 : null, vs, { format: n => n.toFixed(1), unit: ' h' })}
                  hint="From the start and end times of classes held" />
              </div>
              <Card title={`Classes per ${UNIT_LABEL[data.classes.series.unit].toLowerCase()}`}
                subtitle={data.classes.series.points.length < (data.classes.series.unit === 'month' ? 2 : 1) ? undefined : 'Hover or tap a column for its numbers'}
                right={<p className="text-xs text-gray-500">Today so far: <b className="text-gray-800">{num(data.classes.today.held)}</b> held · <b className="text-gray-800">{num(data.classes.today.noShows)}</b> no-shows</p>}>
                <ColumnChart firstColumn={UNIT_LABEL[data.classes.series.unit]}
                  series={[{ name: 'Classes held', color: C.bar }, { name: 'No-shows', color: C.noShow }]}
                  points={data.classes.series.points.map(p => ({
                    key: p.key, label: p.label, title: slotTitle(p, data.classes.series.unit), values: [p.held, p.noShows],
                  }))}
                  note={p => { const att = pctOf(p.values[0], p.values[0] + p.values[1]); return att == null ? null : `${att}% attendance`; }}
                  noteLabel="Attendance" emptyText="No classes logged in this period" />
              </Card>
            </Section>

            {/* ── fees ── */}
            <Section title="Fees" caption={`As of today · ${scope} · everyone, whenever they joined`}
              action={<LinkButton onClick={() => openRevenue({})}>Open Revenue →</LinkButton>}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiTile label="Collected" scope="now" value={inr(data.fees.collected)}
                  hint={`Paid so far by ${plural(data.fees.students, 'student')}, including ${inr(data.fees.regCollected)} in registration fees`} />
                <KpiTile label="Still to collect" scope="now" value={inr(data.fees.outstanding)}
                  hint={`Owed by ${plural(data.fees.owing, 'student')} who haven't paid in full`}
                  onClick={() => openRevenue({ pay: 'owing' })} cta="See who owes" />
                <KpiTile label="Lost — ran away" scope="now" value={inr(data.fees.lost)}
                  hint={`Unpaid fees of ${plural(data.fees.lostStudents, 'student')} marked Ran Away`}
                  onClick={() => openRevenue({ pay: 'ranaway' })} cta="See them" />
                <KpiTile label="Total fees" scope="now" value={inr(data.fees.total)}
                  hint="Collected + still to collect + lost" />
              </div>
              <Card>
                <StackBar format={inr} columns="grid-cols-1 sm:grid-cols-3" parts={[
                  { label: 'Collected', value: data.fees.collected, color: C.good },
                  { label: 'Still to collect', value: data.fees.outstanding, color: C.warning },
                  { label: 'Lost — ran away', value: data.fees.lost, color: C.critical },
                ]} />
                {data.fees.joined && (
                  <p className="text-xs text-gray-500 mt-4">
                    {data.fees.joined.students === 0 ? 'Nobody joined in this period.' : (
                      <>
                        Joined in this period: <b className="text-gray-700">{plural(data.fees.joined.students, 'student')}</b>
                        {' '}(leads not yet with a mentor included) — {inr(data.fees.joined.total)} in fees, {inr(data.fees.joined.collected)} collected from them so far.
                      </>
                    )}
                  </p>
                )}
                {data.fees.feeNotSet > 0 && (
                  <div className="mt-3">
                    <Notice tone="warning" action={<LinkButton onClick={() => openRevenue({ pay: 'nofee' })}>Show them</LinkButton>}>
                      {plural(data.fees.feeNotSet, 'student has', 'students have')} no course fee set, so what they owe isn't counted above.
                    </Notice>
                  </div>
                )}
              </Card>
            </Section>

            {/* ── mentors ── */}
            <Section title="Mentors" caption={`${fmtRange(period.from, period.to)} · a class counts for the mentor who took it`}
              action={<LinkButton onClick={() => openMentorPerf({})}>Open Mentor Performance →</LinkButton>}>
              <Card flush>
                {data.mentors.length === 0 ? (
                  <p className="text-center py-10 text-sm text-gray-400">No mentors match these filters</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                          <th className="px-4 py-2.5">Mentor</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Active students <span className="normal-case font-normal text-gray-400">(now)</span></th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Classes held</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">No-shows</th>
                          <th className="px-4 py-2.5">Attendance</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">Teaching hours</th>
                          <th className="px-4 py-2.5 text-right whitespace-nowrap">At risk <span className="normal-case font-normal text-gray-400">(now)</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...data.mentors].sort((a, b) => b.held - a.held || a.name.localeCompare(b.name)).map(m => (
                          <tr key={m.mentorId} onClick={() => openMentorPerf({ mentor: m.mentorId })}
                            className="border-t border-gray-50 cursor-pointer hover:bg-purple-50/40" title={`Open ${m.name} on Mentor Performance`}>
                            <td className="px-4 py-3">
                              <p className="font-semibold text-gray-800">{m.name}</p>
                              <p className="text-[11px] text-gray-400">{m.role === 'Standard' ? 'Mentor' : m.role}</p>
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">{num(m.activeStudents)}</td>
                            <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-800">{num(m.held)}</td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">{num(m.noShows)}</td>
                            <td className="px-4 py-3"><AttendanceCell pct={m.attPct} /></td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-700">{hours(m.minutes)}</td>
                            <td className="px-4 py-3 text-right">
                              {m.atRisk > 0
                                ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">{m.atRisk}</span>
                                : <span className="text-gray-300">0</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            </Section>

            <HowCounted items={[
              ['In period / Now', 'Tiles marked In period follow the dates you pick. Tiles marked Now are as of today and stay the same whatever the period.'],
              ['Classes held / No-shows', 'Every class log where the student was marked Present / Absent. Mentor Leave and Institute Leave days are neither.'],
              ['Attendance rate', 'Present ÷ (present + absent). 80% or more is Good, 60–79% Fair, under 60% Low.'],
              ['A mentor’s classes', 'A class counts for the mentor who took it — when a substitute covers a class, it counts for the substitute.'],
              ['At risk', 'An active student whose last 3 classes were all absences, whoever took them.'],
              ['No class in 7+ days', "An active student who hasn't attended for 7 days or more — counted from their joining date if they never have."],
              ['Ran away', "Status Ran Away, or deactivated with their payment marked Ran Away on the Payments page. They count for the mentor they're assigned to."],
              ['Fees', "From the Payments records (deleted ones left out). Still to collect = course fee − paid, for students who haven't paid in full. A Ran Away student's unpaid fee is counted as lost instead."],
              ['▲ / ▼ changes', 'Compared with the same stretch just before — this month so far against the same days last month, this week against last week, and so on.'],
            ]} />
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default AdminDashboard;
